import { Platform, type TextStyle } from 'react-native';

export const palette = {
  paper: '#F2EDFB',
  paperSoft: '#E8E1F5',
  paperWarm: '#ECE6F8',

  ink: '#15082E',
  inkSoft: '#3D2D5C',
  inkMute: '#6D607B',

  wine: '#895DFF',
  wineDeep: '#5E37CC',
  wineSoft: '#A98CFF',

  ember: '#E04A88',
  emberSoft: '#FFB4CC',

  gold: '#FFC857',
  sandLilac: '#D9CCEF',
  verdeSlate: '#2F4A6D',

  night: '#0C0814',
  nightSoft: '#1A1228',
  nightInk: '#F5F0FF',
} as const;

export const space = {
  s1: 4,
  s2: 8,
  s3: 12,
  s4: 16,
  s5: 24,
  s6: 32,
  s7: 48,
  s8: 64,
  s9: 96,
  s10: 144,
} as const;

export const radii = {
  xs: 2,
  sm: 6,
  md: 12,
  lg: 22,
  pill: 999,
} as const;

export const fontFamily = {
  display: Platform.select({
    web: '"Instrument Serif", ui-serif, Georgia, serif',
    default: 'InstrumentSerif_400Regular',
  }),
  displayItalic: Platform.select({
    web: '"Instrument Serif", ui-serif, Georgia, serif',
    default: 'InstrumentSerif_400Regular_Italic',
  }),
  body: Platform.select({
    web: 'Geist, -apple-system, system-ui, sans-serif',
    default: 'Geist_400Regular',
  }),
  bodyMedium: Platform.select({
    web: 'Geist, -apple-system, system-ui, sans-serif',
    default: 'Geist_500Medium',
  }),
  bodySemibold: Platform.select({
    web: 'Geist, -apple-system, system-ui, sans-serif',
    default: 'Geist_600SemiBold',
  }),
  mono: Platform.select({
    web: '"JetBrains Mono", ui-monospace, "SF Mono", monospace',
    default: 'JetBrainsMono_400Regular',
  }),
} as const;

type TypeStyle = TextStyle & { fontFamily?: string };

const webClamp = (min: number, vw: number, max: number) =>
  Platform.OS === 'web' ? (`clamp(${min}px, ${vw}vw, ${max}px)` as unknown as number) : max;

const webLh = (multiplier: number, fallbackPx: number) =>
  Platform.OS === 'web' ? (String(multiplier) as unknown as number) : fallbackPx;

export const type: Record<string, TypeStyle> = {
  display1: {
    fontFamily: fontFamily.display,
    fontSize: webClamp(56, 9, 116),
    lineHeight: webLh(1.02, 116),
    letterSpacing: -2,
    fontWeight: '400',
  },
  display2: {
    fontFamily: fontFamily.display,
    fontSize: webClamp(44, 7, 88),
    lineHeight: webLh(1.05, 88),
    letterSpacing: -1.5,
    fontWeight: '400',
  },
  h1: {
    fontFamily: fontFamily.display,
    fontSize: webClamp(36, 5.2, 64),
    lineHeight: webLh(1.08, 64),
    letterSpacing: -1,
    fontWeight: '400',
  },
  h2: {
    fontFamily: fontFamily.display,
    fontSize: webClamp(28, 3.4, 44),
    lineHeight: webLh(1.12, 44),
    letterSpacing: -0.5,
    fontWeight: '400',
  },
  h3: {
    fontFamily: fontFamily.display,
    fontSize: 28,
    lineHeight: 32,
    letterSpacing: -0.25,
    fontWeight: '400',
  },
  h4: {
    fontFamily: fontFamily.bodyMedium,
    fontSize: 20,
    lineHeight: 28,
    letterSpacing: -0.1,
    fontWeight: '500',
  },
  body: {
    fontFamily: fontFamily.body,
    fontSize: 17,
    lineHeight: 26,
    fontWeight: '400',
  },
  bodyLarge: {
    fontFamily: fontFamily.body,
    fontSize: 19,
    lineHeight: 30,
    fontWeight: '400',
  },
  small: {
    fontFamily: fontFamily.body,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '400',
  },
  eyebrow: {
    fontFamily: fontFamily.mono,
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 2,
    textTransform: 'uppercase' as const,
    fontWeight: '500',
  },
  meta: {
    fontFamily: fontFamily.mono,
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 1,
  },
};

export const shadows = {
  card: {
    shadowColor: palette.ink,
    shadowOpacity: 0.08,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
    elevation: 4,
  },
  lift: {
    shadowColor: palette.ink,
    shadowOpacity: 0.16,
    shadowRadius: 40,
    shadowOffset: { width: 0, height: 16 },
    elevation: 8,
  },
  glow: {
    shadowColor: palette.wine,
    shadowOpacity: 0.4,
    shadowRadius: 32,
    shadowOffset: { width: 0, height: 0 },
    elevation: 10,
  },
} as const;

export const breakpoints = {
  mobile: 0,
  tablet: 720,
  desktop: 1024,
  wide: 1440,
} as const;

export const maxContentWidth = 1280;
